/** XeAgents.ai**/
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Info, Users } from "lucide-react";
import { Field } from "./StepCard";
import {
  CALL_SCOPES,
  COMPANY_SIZES,
  INDUSTRIES,
  OTHER_INDUSTRY,
  REGIONS,
  type IcpProfile,
} from "./types";

/**
 * Who is on the other end of the call.
 *
 * The page could write a pitch without this, and did - which is exactly the
 * problem. A value proposition written for nobody in particular is the usual
 * reason a cold call dies in the first ten seconds, and the only way a user
 * could previously say "write this for lenders" was to type it into the copy
 * box, where nothing read it.
 *
 * Three answers now, all required, down from six that were all optional. The
 * two that went - "what hurts today" and a free-text notes box - asked for the
 * brief a second time, after the website read and these three had already given
 * it. What remains is required because Industry is where Target Audience comes
 * from, and Target Audience is spoken on every call.
 *
 * No placeholder text on any input here. "India" and "manual follow-up on
 * overdue EMIs" sat in grey exactly where a real answer would, so a page with
 * nothing filled in looked complete, and the header's "11 required fields still
 * to fill" read as a bug in the page rather than a description of it.
 */
export function IcpPanel({
  icp,
  onChange,
  audienceLine,
  staleAfterEdit,
  error,
}: {
  icp: IcpProfile;
  onChange: (patch: Partial<IcpProfile>) => void;
  /** What the agent will actually say, derived from the industry. */
  audienceLine: string;
  /** Copy already exists and this profile has changed since it was written. */
  staleAfterEdit: boolean;
  error: (key: string) => string | undefined;
}) {
  const needsIndustryName = icp.industry === OTHER_INDUSTRY;

  return (
    <div className="rounded-lg border bg-muted/20 p-4" data-testid="icp-panel">
      <div className="flex items-center gap-2">
        <Users className="h-4 w-4 text-primary" />
        <h3 className="text-sm font-medium">Who are we calling?</h3>
      </div>
      <p className="mt-0.5 text-xs text-muted-foreground">
        This is the brief for the whole script — the agent says it out loud.
      </p>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <Field label="Industry" htmlFor="icp-industry" required error={error('industry')}>
          <Select value={icp.industry} onValueChange={(value) => onChange({ industry: value })}>
            <SelectTrigger id="icp-industry" data-testid="select-icp-industry">
              <SelectValue placeholder="Select an industry" />
            </SelectTrigger>
            <SelectContent>
              {INDUSTRIES.map((industry) => (
                <SelectItem key={industry} value={industry}>
                  {industry}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Company size" htmlFor="icp-size" required error={error('companySize')}>
          <Select value={icp.companySize} onValueChange={(value) => onChange({ companySize: value })}>
            <SelectTrigger id="icp-size" data-testid="select-icp-size">
              <SelectValue placeholder="Select a size" />
            </SelectTrigger>
            <SelectContent>
              {COMPANY_SIZES.map((size) => (
                <SelectItem key={size} value={size}>
                  {size} employees
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        {/* Its own labelled field rather than a box wedged beside the dropdown:
            "Other" is a promise to name the industry, and this is where that
            promise gets kept. Required, because an unnamed Other has told the
            writer exactly as much as picking nothing did. */}
        {needsIndustryName && (
          <Field
            label="Which industry?"
            htmlFor="icp-industry-other"
            required
            error={error('industry')}
            hint="Say it the way your customers would."
            className="sm:col-span-2"
          >
            <Input
              id="icp-industry-other"
              value={icp.industryOther}
              onChange={(event) => onChange({ industryOther: event.target.value })}
              aria-invalid={!!error('industry')}
              data-testid="input-icp-industry-other"
            />
          </Field>
        )}

        <Field label="Region" htmlFor="icp-region" required error={error('region')}>
          <Select value={icp.region} onValueChange={(value) => onChange({ region: value })}>
            <SelectTrigger id="icp-region" data-testid="select-icp-region">
              <SelectValue placeholder="Select a region" />
            </SelectTrigger>
            <SelectContent>
              {REGIONS.map((region) => (
                <SelectItem key={region} value={region}>
                  {region}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label="What the product does on calls" htmlFor="icp-call-scope">
          <Select value={icp.callScope} onValueChange={(value) => onChange({ callScope: value as IcpProfile['callScope'] })}>
            <SelectTrigger id="icp-call-scope" data-testid="select-icp-call-scope">
              <SelectValue placeholder="Optional" />
            </SelectTrigger>
            <SelectContent>
              {CALL_SCOPES.map((scope) => (
                <SelectItem key={scope.value} value={scope.value}>
                  {scope.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>

      {/* Target Audience used to be a box of its own next to these, asking the
          same question again. Showing the line it produces is the part that was
          actually worth keeping: this is spoken on every call. */}
      {audienceLine && (
        <p className="mt-3 text-xs text-muted-foreground" data-testid="text-audience-line">
          The agent will say it calls{" "}
          <span className="font-medium text-foreground">{audienceLine}</span>.
        </p>
      )}

      {/* The AI-written parts update by themselves when the audience changes (see the refresh in
          useTemplateCustomizer); what the user edited is never rewritten, so this points at the rest. */}
      {staleAfterEdit && (
        <p className="mt-3 flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400">
          <Info className="mt-px h-3.5 w-3.5 shrink-0" />
          Audience changed. The AI-written parts update by themselves; sections you edited were left
          as you wrote them — rewrite them in Step 3 to match.
        </p>
      )}
    </div>
  );
}
